import React from "react";
import "../../../css/Common/Components/ContactUs/ContactUs.css";

/**
 * InputField is a reusable React component for rendering input fields or textareas with optional validation.
 * 
 * Props:
 * @param {string} title - The label/title for the input field.
 * @param {string} placeholder - Placeholder text for the input or textarea.
 * @param {boolean} isTextarea - If true, renders a textarea instead of an input.
 * @param {string} name - The name attribute for the input or textarea.
 * @param {boolean} required - If true, marks the field as required.
 * @param {string} specialType - The type attribute for the input (e.g., "text", "date").
 * @param {string|number} min - The minimum value for the input (used for date or number types).
 * @param {string} value - The current value of the input or textarea.
 * @param {function} onChange - Callback function to handle value changes.
 * @param {function} onClick - Callback function to handle click events.
 * 
 * State:
 * @property {string} error - Stores error messages for validation (e.g., when a weekend date is selected).
 * 
 * Features:
 * - Renders either an input or textarea based on `isTextarea`.
 * - For date inputs, prevents selection of weekends and displays an error message.
 * - Calls the parent-provided `onChange` handler after validation.
 * - Displays error messages below the input field when validation fails.
 */
class InputField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: "",
    };
  }
  /**
   * Handles changes to a date input field.
   * 
   * - Checks if the selected date falls on a weekend (Saturday or Sunday).
   * - If a weekend is selected, sets an error message in the component's state and resets the input value.
   * - If a valid weekday is selected, clears any previous error message.
   * - Calls the parent-provided `onChange` handler if it exists.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the date input field.
   */
  handleDateChange = (e) => {
    const selectedDate = new Date(e.target.value);
    const dayOfWeek = selectedDate.getDay();

    // Check if the selected day is Saturday (6) or Sunday (0)
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      // Instead of alerting, set an error message in state
      this.setState({
        error: "Weekends are not allowed. Please select a weekday.",
      });
      e.target.value = ""; // Reset the value if it's a weekend
    } else {
      // Clear any previous error if a valid weekday is selected
      this.setState({ error: "" });
    }

    // Call the parent-provided `onChange` handler if exists
    if (this.props.onChange) {
      this.props.onChange(e);
    }
  };

  render() {
    const {
      title,
      placeholder,
      isTextarea,
      name,
      required,
      specialType,
      min,
      value,
      onChange,
      onClick,
    } = this.props;

    return (
      <div className={`row ${isTextarea ? "textarea-row" : ""}`}>
        <div className="Label-Medium">
          {title}
          {isTextarea ? (
            <textarea
              name={name}
              placeholder={placeholder}
              className="textarea-field"
              required={required}
              value={value}
              onChange={onChange}
            />
          ) : (
            <input
              min={min}
              // onClick={(e) => e.target.showPicker()}
              onClick={onClick}
              type={specialType ? specialType : "text"}
              name={name}
              placeholder={placeholder}
              className="row-textfield"
              required={required}
              value={value}
              onChange={
                specialType === "date" ? this.handleDateChange : onChange
              }
            />
          )}
          {this.state.error && (
            <span className="error-message">{this.state.error}</span>
          )}
        </div>
      </div>
    );
  }
}

export default InputField;
